import { StepBarPlacement } from 'generated/graphql';
import Text from 'common/ui/Text';
import { Stack, Spacer } from 'common/ui/Layout';
import { useCallback } from 'react';
import SettingsDropdown from 'common/SettingsDropdown';
import { useStepBarPlacementSetting } from 'hooks/useViewerSettings';
import { SettingsMenuItem } from 'common/SettingsDropdown/SettingsDropdown';
import { telemetryContextFor } from 'modules/Apollo/types';
import { FF, useSplitFlag } from 'modules/feature-flags';
import { isPhase4Steps } from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/constants';

const STEP_BAR_PLACEMENT_FIELD_LABEL = 'Step bar placement';
const STEP_BAR_PLACEMENT_VALUES = [
  { name: 'Bottom', value: StepBarPlacement.Bottom },
  { name: 'Top', value: StepBarPlacement.Top },
];

export const StepBarPlacementSettings = (): React.ReactElement | null => {
  const [phase] = useSplitFlag(FF.HANGING_PROTOCOL_PHASE);
  const isHPStepsEnabled = isPhase4Steps(phase);
  const [stepBarPlacement, setStepBarPlacement] = useStepBarPlacementSetting({
    context: APOLLO_QUERY_CONTEXT,
  });

  const handleChange = useCallback(
    async (value: StepBarPlacement) => {
      await setStepBarPlacement(value);
    },
    [setStepBarPlacement]
  );

  if (!isHPStepsEnabled) return null;

  return (
    <Stack alignY="center">
      <Text>{STEP_BAR_PLACEMENT_FIELD_LABEL}</Text>
      <Spacer />
      <SettingsDropdown
        value={stepBarPlacement || StepBarPlacement.Bottom}
        onSelectedChange={(value) => {
          handleChange(value as StepBarPlacement);
        }}
        data-testid="step-bar-placement-preference"
      >
        {STEP_BAR_PLACEMENT_VALUES.map((option) => (
          <SettingsMenuItem
            id={`step-bar-placement-menuitem-${option.value}`}
            value={option.value}
            key={option.value}
            data-testid={`${option.value}-menu-item`}
          >
            {option.name}
          </SettingsMenuItem>
        ))}
      </SettingsDropdown>
    </Stack>
  );
};

StepBarPlacementSettings.displayName = 'StepBarPlacementSettings';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(StepBarPlacementSettings);
